import React, { useContext, useEffect, useState } from "react";
import BreadCrumb from "../../components/BreadCrumb";
import { useSelector } from "react-redux";
import { Form, Formik } from "formik";
import StoreSettingApi, {
  GeneralSettingApi,
} from "../../apis/StoreSettings.api";
import LoaderContext from "../../components/loader";

const Storesetting = () => {
  const { handleLoading } = useContext(LoaderContext);
  const [storeSettingData, setStoreSettingData] = useState(null);
  const [generalSettingData, setGeneralSettingData] = useState(null);

  const fetchData = async () => {
    handleLoading(true);
    try {
      const storeRes = await StoreSettingApi.getAllStoreDelivery();
      setStoreSettingData(storeRes.data?.data);

      const generalRes = await GeneralSettingApi.getAllGeneralSetting();
      setGeneralSettingData(generalRes.data?.data);
    } catch (err) {
      console.log("Error fetching data:", err);
    } finally {
      handleLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleGeneralSubmit = async (values) => {
    try {
      const generalId = generalSettingData?.[0]?._id;
      let res;
      if (!generalId) {
        res = await GeneralSettingApi.createGeneralSettingMaster(values);
      } else {
        res = await GeneralSettingApi.updateGeneralSetting(generalId, values);
      }
      console.log("General setting saved:", res.data?.data);
      fetchData();
    } catch (err) {
      console.error("Error saving general settings:", err);
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 min-h-screen">
      <BreadCrumb title="Store Setting" />

      <div className="max-w-7xl mx-auto">
        <GeneralSettings
          generalSettingData={generalSettingData}
          handleGeneralSubmit={handleGeneralSubmit}
        />
      </div>
    </div>
  );
};

const GeneralSettings = ({ generalSettingData, handleGeneralSubmit }) => {
  const { theme } = useSelector((state) => state.theme);
  const isLight = theme === "light";

  const [initialValues, setInitialValues] = useState({
    store_open_time: "10:00",
    store_close_time: "18:00",
    isBusiness: false,
    isBusy: false,
    isVisible: false,
    all_day_open: false,
    sunday_open: false,
    monday_open: false,
    tuesday_open: false,
    wednesday_open: false,
    thursday_open: false,
    friday_open: false,
    saturday_open: false,
  });

  useEffect(() => {
    if (generalSettingData?.length > 0) {
      const data = generalSettingData[0];
      setInitialValues({
        store_open_time: data.store_open_time || "10:00",
        store_close_time: data.store_close_time || "18:00",
        isBusiness: !!data.isBusiness,
        isBusy: !!data.isBusy,
        isVisible: !!data.isVisible,
        all_day_open: !!data.all_day_open,
        sunday_open: !!data.sunday_open,
        monday_open: !!data.monday_open,
        tuesday_open: !!data.tuesday_open,
        wednesday_open: !!data.wednesday_open,
        thursday_open: !!data.thursday_open,
        friday_open: !!data.friday_open,
        saturday_open: !!data.saturday_open,
      });
    }
  }, [generalSettingData]);

  const saveChanges = async (updatedValues) => {
    try {
      await handleGeneralSubmit(updatedValues);
      console.log("Changes saved successfully");
    } catch (err) {
      console.error("Save failed:", err);
    }
  };

  const handleAllDaysToggle = (setFieldValue, values) => {
    const newValue = !values.all_day_open;
    const updates = {
      all_day_open: newValue,
      sunday_open: newValue,
      monday_open: newValue,
      tuesday_open: newValue,
      wednesday_open: newValue,
      thursday_open: newValue,
      friday_open: newValue,
      saturday_open: newValue,
    };

    Object.entries(updates).forEach(([key, val]) => setFieldValue(key, val));
    saveChanges({ ...values, ...updates });
  };

  const handleDayToggle = (day, setFieldValue, values) => {
    const dayKey = `${day.toLowerCase()}_open`;
    const newValue = !values[dayKey];

    setFieldValue(dayKey, newValue);

    const days = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];
    const allOpen = days.every((d) =>
      d === day.toLowerCase() ? newValue : values[`${d}_open`],
    );

    setFieldValue("all_day_open", allOpen);

    saveChanges({
      ...values,
      [dayKey]: newValue,
      all_day_open: allOpen,
    });
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={() => {}}
    >
      {({ handleChange, setFieldValue, values }) => (
        <div
          className={`
            ${isLight ? "bg-white border-gray-200" : "bg-gray-800 border-gray-700"}
            shadow-lg rounded-xl border overflow-hidden
          `}
        >
          <div className="p-5 md:p-6 lg:p-8">
            <div className="flex flex-col lg:flex-row lg:gap-10 xl:gap-16">
              {/* Left column - Time & Status toggles */}
              <div className="flex-1 min-w-0">
                <h3
                  className={`
                    text-lg font-semibold mb-5
                    ${isLight ? "text-gray-800" : "text-gray-100"}
                  `}
                >
                  General Settings
                </h3>

                {/* Store Hours */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  {/* Store Open Time */}
                  <div>
                    <label
                      className={`
        block text-sm font-medium mb-2
        ${isLight ? "text-gray-700" : "text-gray-300"}
      `}
                    >
                      Store Open Time
                    </label>
                    <input
                      type="time"
                      name="store_open_time"
                      value={values.store_open_time || ""}
                      onChange={(e) => {
                        handleChange(e);
                        saveChanges({
                          ...values,
                          store_open_time: e.target.value,
                        });
                      }}
                      className={`
        w-full p-2 text-base
        border rounded-lg
        focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500
        transition-colors duration-150
        ${
          isLight
            ? "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
            : "bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-500"
        }
      `}
                    />
                  </div>

                  {/* Store Close Time */}
                  <div>
                    <label
                      className={`
        block text-sm font-medium mb-2
        ${isLight ? "text-gray-700" : "text-gray-300"}
      `}
                    >
                      Store Close Time
                    </label>
                    <input
                      type="time"
                      name="store_close_time"
                      value={values.store_close_time || ""}
                      onChange={(e) => {
                        handleChange(e);
                        saveChanges({
                          ...values,
                          store_close_time: e.target.value,
                        });
                      }}
                      className={`
        w-full p-2 text-base
        border rounded-lg
        focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500
        transition-colors duration-150
        ${
          isLight
            ? "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
            : "bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-500"
        }
      `}
                    />
                  </div>
                </div>

                <hr
                  className={`my-8 ${isLight ? "border-gray-200" : "border-gray-700"}`}
                />

                {/* Status Toggles */}
                <div className="space-y-5">
                  {[
                    { key: "isBusiness", label: "Business" },
                    { key: "isBusy", label: "Busy" },
                    { key: "isVisible", label: "Visible" },
                  ].map(({ key, label }) => (
                    <div
                      key={key}
                      className="flex items-center justify-between"
                    >
                      <span
                        className={`
                          text-base font-medium
                          ${isLight ? "text-gray-700" : "text-gray-300"}
                        `}
                      >
                        {label}
                      </span>

                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={values[key]}
                          onChange={() => {
                            const newVal = !values[key];
                            setFieldValue(key, newVal);
                            saveChanges({ ...values, [key]: newVal });
                          }}
                          className={`
                             rounded border
                            ${
                              isLight
                                ? "bg-gray-100 border-gray-300 text-green-600 focus:ring-green-500"
                                : "bg-gray-700 border-gray-600 text-green-600 focus:ring-green-500"
                            }
                          `}
                        />
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right column - Days settings */}
              <div className="flex-1 min-w-0 mt-10 lg:mt-0">
                <h3
                  className={`
                    text-lg font-semibold mb-5
                    ${isLight ? "text-gray-800" : "text-gray-100"}
                  `}
                >
                  Store Days Settings
                </h3>

                <div
                  className={`
                    rounded-xl p-5 border
                    ${
                      isLight
                        ? "bg-gray-50 border-gray-200"
                        : "bg-gray-900/50 border-gray-700"
                    }
                  `}
                >
                  {[
                    {
                      label: "All Days",
                      key: "all_day_open",
                      handler: () => handleAllDaysToggle(setFieldValue, values),
                    },
                    ...[
                      "Sunday",
                      "Monday",
                      "Tuesday",
                      "Wednesday",
                      "Thursday",
                      "Friday",
                      "Saturday",
                    ].map((day) => ({
                      label: day,
                      key: `${day.toLowerCase()}_open`,
                      handler: () =>
                        handleDayToggle(day, setFieldValue, values),
                    })),
                  ].map(({ label, key, handler }) => (
                    <label
                      key={key}
                      className={`
                        flex items-center justify-between py-3 border-b last:border-b-0
                        ${isLight ? "border-gray-200" : "border-gray-700"}
                      `}
                    >
                      <span
                        className={`
                          text-base
                          ${isLight ? "text-gray-700" : "text-gray-300"}
                        `}
                      >
                        {label}
                      </span>
                      <input
                        type="checkbox"
                        checked={values[key]}
                        onChange={handler}
                        className={`
                           rounded border
                          ${
                            isLight
                              ? "bg-gray-100 border-gray-300 text-green-600 focus:ring-green-500"
                              : "bg-gray-700 border-gray-600 text-green-600 focus:ring-green-500"
                          }
                        `}
                      />
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Formik>
  );
};

export default Storesetting;
